import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { API_ENDPOINTS, getCurrentBrand } from '../config/api';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Menu,
  MenuItem,
  Fade,
  Skeleton,
  Pagination,
  LinearProgress,
  Paper,
  Tooltip,
  Avatar,
  Stack,
  Container,
  CardMedia,
  CardActions,
  Zoom,
  Slide,
  TextField,
  Autocomplete,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Image as ImageIcon,
  Visibility as ViewIcon,
  CloudUpload as UploadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Close as CloseIcon,
  PlayArrow as PlayArrowIcon,
  Category as CategoryIcon,
  FilterList as FilterListIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  AutoAwesome as AutoAwesomeIcon,
  SelectAll as SelectAllIcon,
  PlaylistAddCheck as PlaylistAddCheckIcon,
  Block as BlockIcon
} from '@mui/icons-material';

import { RadioButtonUnchecked as RadioButtonUncheckedIcon } from '@mui/icons-material';
import { heicTo } from 'heic-to';

// Helper to render active/inactive status chip
const StatusChip = ({ active, onToggle, loading }) => {
  const { t } = useTranslation();
  const isActive = active;
  const iconColor = isActive ? '#27ae60' : '#e74c3c';

  const handleClick = (event) => {
    event.stopPropagation();
    if (!loading && onToggle) {
      onToggle();
    }
  };

  return (
    <Chip
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
            {isActive ? t('aiGenerator.active') || 'Active' : t('aiGenerator.inactive') || 'Inactive'}
          </Typography>
          {loading ? (
            <CircularProgress size={14} sx={{ color: iconColor }} />
          ) : (
            isActive ? <CheckCircleIcon sx={{ fontSize: 16, color: iconColor }} /> : <ErrorIcon sx={{ fontSize: 16, color: iconColor }} />
          )}
        </Box>
      }
      size="small"
      variant="outlined"
      clickable={Boolean(onToggle)}
      onClick={handleClick}
      disabled={loading}
      sx={{
        pr: { xs: 0.75, sm: 1 },
        pl: { xs: 0.75, sm: 1 },
        height: 24,
        borderRadius: 2,
        fontWeight: 700,
        letterSpacing: 0.3,
        bgcolor: isActive ? '#e8f5e9' : '#ffebee',
        color: isActive ? '#2e7d32' : '#c62828',
        borderColor: isActive ? '#66bb6a' : '#ef5350',
        borderWidth: 1.5,
        flexShrink: 0,
        cursor: onToggle && !loading ? 'pointer' : 'default',
        opacity: loading ? 0.75 : 1,
        transition: 'all 0.2s ease',
        '&:hover': onToggle && !loading ? {
          transform: 'translateY(-1px)',
          bgcolor: isActive ? '#c8e6c9' : '#ffcdd2',
          boxShadow: '0 4px 12px rgba(39, 174, 96, 0.2)'
        } : undefined,
        '& .MuiChip-label': {
          px: 0,
          fontSize: { xs: '0.7rem', sm: '0.75rem' },
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          whiteSpace: 'nowrap'
        }
      }}
    />
  );
};

// Memoized Image Card Component for better performance
const IgImageCard = React.memo(({
  image,
  index,
  selectedIds,
  handleImageClick,
  handleToggleStatus,
  statusUpdatingId,
  t,
  isVideo,
  handleMenuOpen,
  toggleSelect
}) => {
  return (
    <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4}>
      <Zoom in timeout={300 + ((index % 12) * 50)}>
        <Card
          sx={{
            borderRadius: 4,
            overflow: 'hidden',
            cursor: 'pointer',
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            border: selectedIds.has(image.id) ? '2px solid #1d4ed8' : '2px solid transparent',
            background: 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #2c3e50 0%, #3498db 100%) border-box',
            position: 'relative',
            '@media (hover: hover)': {
              '&:hover': {
                transform: 'translateY(-12px) scale(1.03)',
                boxShadow: '0 25px 50px rgba(52, 152, 219, 0.25)',
                '&::before': {
                  opacity: 1
                }
              }
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(135deg, rgba(44, 62, 80, 0.1) 0%, rgba(52, 152, 219, 0.1) 100%)',
              opacity: 0,
              transition: 'opacity 0.4s ease',
              zIndex: 1,
              pointerEvents: 'none'
            }
          }}
          onClick={() => handleImageClick(image)}
        >
          <Box sx={{ position: 'relative', zIndex: 2, width: '100%', height: { xs: 240, sm: 280, md: 300 }, overflow: 'hidden' }}>
            <Box
              sx={{
                position: 'absolute',
                top: 12,
                left: 12,
                zIndex: 3,
                display: 'flex',
                gap: 1
              }}
            >
              <StatusChip
                active={image.is_active !== false}
                onToggle={() => handleToggleStatus(image)}
                loading={statusUpdatingId === image.id}
              />
            </Box>
            {!image.image_url ? (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  p: 4,
                  pt: 8,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  background: 'linear-gradient(145deg, #fff4fb 0%, #f5efff 100%)'
                }}
              >
                <Typography variant="overline" sx={{ color: '#d63384', fontWeight: 800, letterSpacing: 2 }}>
                  Instagram Caption
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mt: 1.5,
                    color: '#263238',
                    fontWeight: 600,
                    lineHeight: 1.7,
                    display: '-webkit-box',
                    WebkitLineClamp: 7,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    whiteSpace: 'pre-line'
                  }}
                >
                  {image.caption}
                </Typography>
              </Box>
            ) : isVideo(image.image_url) ? (
              <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                <CardMedia
                  component="video"
                  src={image.image_url}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'scale(1.05)'
                    }
                  }}
                  preload="metadata"
                  muted
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    bgcolor: 'rgba(0, 0, 0, 0.6)',
                    borderRadius: '50%',
                    width: 56,
                    height: 56,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                    zIndex: 3
                  }}
                >
                  <PlayArrowIcon sx={{ fontSize: 32, color: 'white', ml: 0.5 }} />
                </Box>
              </Box>
            ) : (
              <CardMedia
                component="img"
                image={image.image_url}
                alt={`Instagram Image #${image.id}`}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  filter: 'brightness(1) saturate(1.1)',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    filter: 'brightness(1.1) saturate(1.2)'
                  }
                }}
                loading="lazy"
                decoding="async"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            )}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                minHeight: { xs: 240, sm: 280, md: 300 },
                display: 'none',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                flexDirection: 'column'
              }}
            >
              <Avatar sx={{
                bgcolor: 'rgba(52, 152, 219, 0.1)',
                mb: 2,
                width: 48,
                height: 48,
                animation: 'float 3s ease-in-out infinite'
              }}>
                <ImageIcon sx={{ fontSize: 28, color: '#3498db' }} />
              </Avatar>
              <Typography variant="body2" sx={{ color: '#6c757d', fontWeight: 500 }}>
                {t('igImages.imageNotAvailable')}
              </Typography>
            </Box>

            {/* Overlay Actions */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, transparent 30%, transparent 70%, rgba(0,0,0,0.3) 100%)',
                opacity: 0,
                transition: 'opacity 0.3s ease',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'flex-end',
                p: 2
              }}
              className="card-overlay"
            >
              <Stack direction="row" spacing={1}>
                <Tooltip title={t('igImages.viewDetails')}>
                  <IconButton
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.9)',
                      color: '#3498db',
                      '&:hover': {
                        bgcolor: 'white',
                        transform: 'scale(1.1)'
                      }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleImageClick(image);
                    }}
                  >
                    <ViewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('igImages.moreOptions')}>
                  <IconButton
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.9)',
                      color: '#3498db',
                      '&:hover': {
                        bgcolor: 'white',
                        transform: 'scale(1.1)'
                      }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMenuOpen(e, image);
                    }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>

          </Box>

          <CardActions sx={{ p: 3, pt: 2, pb: 2, minHeight: 72, justifyContent: 'center', position: 'relative', zIndex: 2 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); toggleSelect(image.id); }}
                sx={{
                  bgcolor: selectedIds.has(image.id) ? 'rgba(29,78,216,0.12)' : 'rgba(255,255,255,0.9)',
                  color: selectedIds.has(image.id) ? '#1d4ed8' : '#334155',
                  border: '1px solid',
                  borderColor: selectedIds.has(image.id) ? 'rgba(29,78,216,0.35)' : '#cbd5e1',
                  borderRadius: 2,
                  width: { xs: 48, sm: 36 }, // Larger touch target on mobile
                  height: { xs: 48, sm: 36 }, // Larger touch target on mobile
                  boxShadow: selectedIds.has(image.id) ? '0 4px 12px rgba(29,78,216,0.25)' : 'none',
                  '&:hover': {
                    bgcolor: selectedIds.has(image.id) ? 'rgba(29,78,216,0.18)' : 'white'
                  }
                }}
                aria-label={selectedIds.has(image.id) ? 'Selected' : 'Select'}
              >
                {selectedIds.has(image.id) ? (
                  <CheckCircleIcon fontSize="small" />
                ) : (
                  <RadioButtonUncheckedIcon fontSize="small" />
                )}
              </IconButton>

              <Button
                size="medium"
                startIcon={<ViewIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleImageClick(image);
                }}
                sx={{
                  background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  boxShadow: selectedIds.has(image.id) ? '0 0 0 2px #1d4ed8 inset' : '0 4px 15px rgba(52, 152, 219, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1a252f 0%, #2980b9 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(52, 152, 219, 0.4)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                {t('igImages.viewDetails')}
              </Button>
            </Stack>
          </CardActions>
        </Card>
      </Zoom>
    </Grid>
  );
});

const IgImages = () => {

  const { t } = useTranslation();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedImage, setSelectedImage] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [captionDialogOpen, setCaptionDialogOpen] = useState(false);
  const [newCaption, setNewCaption] = useState('');
  const [newCaptionCategory, setNewCaptionCategory] = useState('');
  const [savingNewCaption, setSavingNewCaption] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateFiles, setDuplicateFiles] = useState([]);
  const [quota, setQuota] = useState({ quota_limit: 100, used_quota: 0 });
  const [categoryFilter, setCategoryFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [isEditingCategory, setIsEditingCategory] = useState(false);
  const [editedCategory, setEditedCategory] = useState('');
  const [savingCategory, setSavingCategory] = useState(false);
  const [editedCaption, setEditedCaption] = useState('');
  const [savingCaption, setSavingCaption] = useState(false);
  const [contentFilter, setContentFilter] = useState('active'); // 'active', 'inactive', 'all'
  const [contentTypeFilter, setContentTypeFilter] = useState('captions');
  const [activatingAll, setActivatingAll] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  // Helper function to check if URL is a video
  const isVideo = (url) => {
    if (!url) return false;
    const videoExtensions = ['.mp4', '.mov', '.avi', '.webm', '.mkv', '.flv', '.wmv', '.m4v'];
    const lowerUrl = url.toLowerCase();
    return videoExtensions.some(ext => lowerUrl.includes(ext));
  };

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const brand = getCurrentBrand();
      const response = await fetch(`${API_ENDPOINTS.CATEGORIES}?brand=${brand}&platform=instagram`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  // Fetch images from API
  const fetchImages = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const offset = (page - 1) * itemsPerPage;
      const params = new URLSearchParams({
        limit: itemsPerPage.toString(),
        offset: offset.toString(),
        brand: getCurrentBrand(),
        type: contentTypeFilter
      });

      if (categoryFilter) {
        params.append('category', categoryFilter);
      }

      // Add status filter
      // Only send 'active' parameter when filtering by active or inactive
      // When filter is 'all', don't send the parameter so backend returns everything
      if (contentFilter === 'active') {
        params.append('active', 'true');
      } else if (contentFilter === 'inactive') {
        params.append('active', 'false');
      }
      // When contentFilter === 'all', don't add any active parameter

      const response = await fetch(`${API_ENDPOINTS.IG_IMAGES}?${params}`);

      if (!response.ok) {
        throw new Error('Failed to fetch images');
      }

      const data = await response.json();
      const returnedItems = Array.isArray(data.images) ? data.images : [];
      const visibleItems = contentTypeFilter === 'captions'
        ? returnedItems.filter((item) => typeof item.caption === 'string' && item.caption.trim().length > 0 && !item.image_url)
        : returnedItems.filter((item) => typeof item.image_url === 'string' && item.image_url.trim().length > 0);
      const responseIncludedWrongType = visibleItems.length !== returnedItems.length;

      setImages(visibleItems);
      setTotalCount(responseIncludedWrongType ? visibleItems.length : (data.totalCount || 0));
    } catch (err) {
      console.error('Error fetching images:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [itemsPerPage, categoryFilter, contentFilter, contentTypeFilter]);

  // Reset page when filters change and refetch
  useEffect(() => {
    setCurrentPage(1);
    // The fetchImages will be called automatically when currentPage changes
  }, [categoryFilter, contentFilter, contentTypeFilter]);

  // Handle activate all inactive content
  const handleActivateAllInactive = async () => {
    // This is a client-side estimation, real count comes from backend or current view
    // For safety, we'll confirm generic action
    if (!window.confirm(t('igImages.activateAllInactiveConfirm') || 'Are you sure you want to activate all inactive images?')) {
      return;
    }

    setActivatingAll(true);
    setError(null);

    try {
      const brand = getCurrentBrand();
      // We need to fetch all inactive images first or have a bulk endpoint
      // Since we don't have a bulk endpoint documented, we'll iterate over visible inactive ones 
      // OR better, we assume the user wants to activate what they see or we need a bulk endpoint.
      // Given the XHS implementation iterates, we will do the same but we need to know IDs.
      // For now, let's implement a bulk update for the current view if filtered by inactive, 
      // or fetch all inactive IDs. 
      // To match XHS implementation exactly:

      // 1. Fetch all inactive images (limit high)
      const params = new URLSearchParams({
        limit: '1000', // Fetch a large batch to get all inactive images
        offset: '0',
        brand: brand,
        active: 'false'
      });

      const response = await fetch(`${API_ENDPOINTS.IG_IMAGES}?${params}`);
      if (!response.ok) throw new Error('Failed to fetch inactive images');

      const data = await response.json();
      const inactiveImages = data.images || [];

      if (inactiveImages.length === 0) {
        alert(t('igImages.noInactiveFound') || 'No inactive images found to activate.');
        setActivatingAll(false);
        return;
      }

      let successCount = 0;
      let failCount = 0;

      for (const img of inactiveImages) {
        try {
          const updateResponse = await fetch(`${API_ENDPOINTS.IG_IMAGES}/${img.id}?brand=${brand}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              image_url: img.image_url,
              category: img.category,
              is_active: true
            })
          });

          if (updateResponse.ok) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (err) {
          console.error(`Error activating image ${img.id}:`, err);
          failCount++;
        }
      }

      // Refresh
      await fetchImages(currentPage);
      await fetchQuota();

      if (failCount > 0) {
        setError(`${successCount} images activated, ${failCount} failed.`);
      } else {
        setError(null); // Success
      }

    } catch (err) {
      console.error('Error activating all:', err);
      setError(err.message);
    } finally {
      setActivatingAll(false);
    }
  };

  // Handle toggle status
  const handleToggleStatus = async (image) => {
    if (!image?.id) return;

    const currentActive = image.is_active !== false;
    const nextStatus = !currentActive;

    const confirmationMessage = currentActive
      ? t('igImages.confirmDeactivate') || 'Deactivate this image?'
      : t('igImages.confirmActivate') || 'Activate this image?';

    if (!window.confirm(confirmationMessage)) return;

    setStatusUpdatingId(image.id);

    // Optimistic update
    setImages(prev => prev.map(img =>
      img.id === image.id ? { ...img, is_active: nextStatus } : img
    ));

    try {
      const brand = getCurrentBrand();
      const response = await fetch(`${API_ENDPOINTS.IG_IMAGES}/${image.id}?brand=${brand}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image_url: image.image_url,
          category: image.category,
          is_active: nextStatus
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update status');
      }

      // Refresh quota if needed (activating might consume quota?) - usually upload does.
      // But let's refresh just in case logic changes.

    } catch (err) {
      console.error('Error updating status:', err);
      // Revert optimistic update
      setImages(prev => prev.map(img =>
        img.id === image.id ? { ...img, is_active: currentActive } : img
      ));
      setError(err.message);
    } finally {
      setStatusUpdatingId(null);
    }
  };

  // Fetch quota
  const fetchQuota = useCallback(async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.QUOTA_BY_PLATFORM('instagram')}?brand=${getCurrentBrand()}`);
      if (response.ok) {
        const quotaData = await response.json();
        setQuota(quotaData);
      }
    } catch (err) {
      console.error('Error fetching quota:', err);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchImages(currentPage);
    fetchQuota();
    fetchCategories();
  }, [currentPage, fetchImages, fetchQuota, fetchCategories]);

  // Handle page change
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const selectAllOnCurrentPage = () => {
    const ids = images.map(img => img.id).filter(Boolean);
    setSelectedIds(new Set(ids));
  };

  const selectAllAcrossPages = async () => {
    setBulkLoading(true);
    try {
      const params = new URLSearchParams({
        limit: '10000', // Large limit to fetch all
        offset: '0',
        brand: getCurrentBrand()
      });

      if (categoryFilter) params.append('category', categoryFilter);

      // Add status filter
      if (contentFilter === 'active') {
        params.append('active', 'true');
      } else if (contentFilter === 'inactive') {
        params.append('active', 'false');
      }

      const response = await fetch(`${API_ENDPOINTS.IG_IMAGES}?${params}`);
      if (!response.ok) throw new Error('Failed to fetch all images');

      const data = await response.json();
      const allImages = data.images || [];
      const ids = allImages.map(img => img.id).filter(Boolean);
      setSelectedIds(new Set(ids));
    } catch (err) {
      console.error('Error selecting all:', err);
      setError(err.message);
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkActivate = async () => {
    if (selectedIds.size === 0) return;
    setBulkLoading(true);
    const brand = getCurrentBrand();
    let success = 0, fail = 0;
    for (const id of selectedIds) {
      const img = images.find(i => i.id === id);
      if (!img) { fail++; continue; }
      try {
        const res = await fetch(`${API_ENDPOINTS.IG_IMAGES}/${id}?brand=${brand}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            image_url: img.image_url,
            category: img.category,
            is_active: true
          })
        });
        if (res.ok) {
          success++;
        } else {
          fail++;
        }
      } catch (e) {
        console.error('Bulk activate error:', e);
        fail++;
      }
    }
    await fetchImages(currentPage);
    setBulkLoading(false);
    clearSelection();
    if (fail > 0) setError(`${success} activated, ${fail} failed.`); else setError(null);
  };

  const handleBulkDeactivate = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(t('igImages.confirmBulkDeactivate') || 'Deactivate selected items?')) return;
    setBulkLoading(true);
    const brand = getCurrentBrand();
    let success = 0, fail = 0;
    for (const id of selectedIds) {
      const img = images.find(i => i.id === id);
      if (!img) { fail++; continue; }
      try {
        const res = await fetch(`${API_ENDPOINTS.IG_IMAGES}/${id}?brand=${brand}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            image_url: img.image_url,
            category: img.category,
            is_active: false
          })
        });
        if (res.ok) {
          success++;
        } else {
          fail++;
        }
      } catch (e) {
        console.error('Bulk deactivate error:', e);
        fail++;
      }
    }
    await fetchImages(currentPage);
    setBulkLoading(false);
    clearSelection();
    if (fail > 0) setError(`${success} deactivated, ${fail} failed.`); else setError(null);
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(t('igImages.confirmBulkDelete') || 'Delete selected items? This cannot be undone.')) return;
    setBulkLoading(true);
    const brand = getCurrentBrand();
    let success = 0, fail = 0;
    for (const id of selectedIds) {
      try {
        const res = await fetch(`${API_ENDPOINTS.IG_IMAGES}/${id}?brand=${brand}`, { method: 'DELETE' });
        if (res.ok) {
          success++;
        } else {
          fail++;
        }
      } catch (e) {
        console.error('Bulk delete error:', e);
        fail++;
      }
    }
    await fetchImages(currentPage);
    setBulkLoading(false);
    clearSelection();
    if (fail > 0) setError(`${success} deleted, ${fail} failed.`); else setError(null);
  };

  // Handle image click
  const handleImageClick = (image) => {
    setSelectedImage(image);
    setEditedCaption(image.caption || '');
    setIsEditingCategory(false);
    setEditedCategory('');
    setDialogOpen(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedImage(null);
    setEditedCaption('');
    setIsEditingCategory(false);
    setEditedCategory('');
  };

  // Handle menu open
  const handleMenuOpen = (event, image) => {
    setAnchorEl(event.currentTarget);
    setSelectedImage(image);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedImage(null);
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = [];
    const errors = [];

    files.forEach(file => {
      // Validate file type (images and videos)
      // Allow HEIC/HEIF explictly by extension even if type doesn't match image/
      const isHeic = file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif');
      const isImage = file.type.startsWith('image/') || isHeic;
      const isVideo = file.type.startsWith('video/');

      if (!isImage && !isVideo) {
        errors.push(`${file.name}: Not an image or video file`);
        return;
      }

      // Validate file size (max 50MB for videos, 10MB for images)
      const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
      if (file.size > maxSize) {
        errors.push(`${file.name}: File size must be less than ${isVideo ? '50MB' : '10MB'}`);
        return;
      }
      validFiles.push(file);
    });

    if (errors.length > 0) {
      setError(errors.join(', '));
    } else {
      setError(null);
    }

    setSelectedFiles(validFiles);
    // Reset input value to allow selecting same files again if needed
    event.target.value = '';
  };

  // Handle perform upload (logic extracted from handleUpload)
  const performUpload = async (filesToUpload = selectedFiles) => {
    if (filesToUpload.length === 0) {
      setError(t('igImages.selectFilesUpload'));
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setUploadProgress(0);
      setUploadingFiles([]);

      const totalFiles = filesToUpload.length;
      let completedFiles = 0;
      const results = [];

      for (let i = 0; i < filesToUpload.length; i++) {
        let file = filesToUpload[i];

        // Update uploading files list
        setUploadingFiles(prev => [...prev, { name: file.name, status: 'uploading' }]);

        try {
          // Serialize HEIC conversion here
          if (file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif')) {
            try {
              const blob = await heicTo({ blob: file, type: 'image/jpeg' });
              const newName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
              file = new File([blob], newName, { type: 'image/jpeg' });
            } catch (convErr) {
              throw new Error(`HEIC conversion failed: ${convErr.message}`);
            }
          }

          // Create FormData
          const formData = new FormData();
          formData.append('image', file);

          // Upload to Cloudflare R2 with IG folder
          const uploadResponse = await fetch(`${API_ENDPOINTS.UPLOAD_TO_R2}?folder=lokss2-ig`, {
            method: 'POST',
            body: formData,
          });

          if (!uploadResponse.ok) {
            throw new Error(`Failed to upload ${file.name} to R2`);
          }

          const uploadResult = await uploadResponse.json();

          // Insert into lokss2_ig table
          const insertResponse = await fetch(`${API_ENDPOINTS.IG_IMAGES}?brand=${getCurrentBrand()}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              image_url: uploadResult.url,
              caption: null,
              is_active: true,
              category: selectedCategory || null
            }),
          });

          if (!insertResponse.ok) {
            const errorData = await insertResponse.json();
            if (insertResponse.status === 403 && errorData.error === 'Quota exceeded') {
              throw new Error(errorData.message || 'Quota exceeded');
            }
            throw new Error(`Failed to save ${file.name} to database`);
          }

          // Refresh quota after successful upload
          await fetchQuota();

          // Update status to completed
          setUploadingFiles(prev =>
            prev.map(f => f.name === filesToUpload[i].name ? { ...f, status: 'completed' } : f)
          );

          results.push({ file: file.name, success: true, url: uploadResult.url });
          completedFiles++;
          setUploadProgress((completedFiles / totalFiles) * 100);

        } catch (fileError) {
          console.error(`Error uploading ${file.name}:`, fileError);
          setUploadingFiles(prev =>
            prev.map(f => f.name === filesToUpload[i].name ? { ...f, status: 'error', error: fileError.message } : f)
          );
          results.push({ file: file.name, success: false, error: fileError.message });
          completedFiles++;
          setUploadProgress((completedFiles / totalFiles) * 100);
        }
      }

      const successful = results.filter(r => r.success).length;
      const failed = results.filter(r => !r.success).length;

      if (failed > 0) {
        const failedFiles = results.filter(r => !r.success).map(r => `${r.file}: ${r.error}`).join(', ');
        setError(`${successful} ${t('igImages.filesUploaded')}, ${failed} ${t('igImages.failedFiles')}: ${failedFiles}`);
      } else {
        setError(null);
      }

      // Close dialog and refresh images after a short delay
      setTimeout(() => {
        setUploadDialogOpen(false);
        setSelectedFiles([]);
        setUploadProgress(0);
        setUploadingFiles([]);
        setSelectedCategory('');
        fetchImages(currentPage);
        fetchQuota();
        fetchCategories(); // Refresh categories in case a new one was added
      }, 2000);

    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  // Handle upload check
  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setError(t('igImages.selectFilesUpload'));
      return;
    }

    try {
      setUploading(true);
      const params = new URLSearchParams({
        limit: '10000',
        brand: getCurrentBrand()
      });
      const response = await fetch(`${API_ENDPOINTS.IG_IMAGES}?${params}`);

      if (response.ok) {
        const data = await response.json();
        const existingImages = data.images || [];

        const duplicates = selectedFiles.filter(file => {
          return existingImages.some(img =>
            img.image_url && img.image_url.toLowerCase().includes(file.name.toLowerCase())
          );
        }).map(f => f.name);

        if (duplicates.length > 0) {
          setDuplicateFiles(duplicates);
          setDuplicateDialogOpen(true);
          setUploading(false);
          return;
        }
      }
    } catch (err) {
      console.error('Error checking duplicates:', err);
    }

    await performUpload();
  };

  const handleConfirmUpload = async () => {
    setDuplicateDialogOpen(false);
    await performUpload();
  };

  const handleSkipDuplicates = async () => {
    const filesToUpload = selectedFiles.filter(file => !duplicateFiles.includes(file.name));
    setDuplicateDialogOpen(false);

    if (filesToUpload.length === 0) {
      setUploadDialogOpen(false);
      setSelectedFiles([]);
      return;
    }

    await performUpload(filesToUpload);
  };

  // Close upload dialog
  const handleCloseUploadDialog = () => {
    setUploadDialogOpen(false);
    setSelectedFiles([]);
    setUploadProgress(0);
    setUploadingFiles([]);
    setSelectedCategory('');
    setError(null);
  };

  // Handle image deletion
  const handleDeleteImage = async (imageId, isCaption = false) => {
    const confirmationMessage = isCaption
      ? 'Are you sure you want to delete this caption? This cannot be undone.'
      : t('igImages.deleteConfirm');

    if (!window.confirm(confirmationMessage)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const brand = getCurrentBrand();
      const response = await fetch(`${API_ENDPOINTS.IG_IMAGES}/${imageId}?brand=${brand}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(isCaption ? 'Failed to delete caption' : t('igImages.failedToDeleteImage'));
      }

      // Refresh the images list
      await fetchImages(currentPage);

      if (selectedImage?.id === imageId) {
        handleCloseDialog();
      }

    } catch (err) {
      console.error(`Error deleting ${isCaption ? 'caption' : 'image'}:`, err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle category edit start
  const handleStartEditCategory = () => {
    setEditedCategory(selectedImage?.category || '');
    setIsEditingCategory(true);
  };

  // Handle category edit cancel
  const handleCancelEditCategory = () => {
    setIsEditingCategory(false);
    setEditedCategory('');
  };

  // Handle category save
  const handleSaveCategory = async () => {
    if (!selectedImage) return;

    try {
      setSavingCategory(true);
      setError(null);

      const brand = getCurrentBrand();
      const response = await fetch(`${API_ENDPOINTS.IG_IMAGES}/${selectedImage.id}?brand=${brand}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          image_url: selectedImage.image_url,
          is_active: selectedImage.is_active,
          category: editedCategory || null
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update category');
      }

      const updatedImage = await response.json();

      // Update selected image
      setSelectedImage(updatedImage);

      // Update in images list
      setImages(prevImages =>
        prevImages.map(img =>
          img.id === updatedImage.id ? updatedImage : img
        )
      );

      // Refresh categories if new category was added
      if (editedCategory && !categories.includes(editedCategory)) {
        await fetchCategories();
      }

      setIsEditingCategory(false);
      setEditedCategory('');
    } catch (err) {
      console.error('Error updating category:', err);
      setError(err.message || 'Failed to update category');
    } finally {
      setSavingCategory(false);
    }
  };

  const handleSaveCaption = async () => {
    if (!selectedImage) return;

    try {
      setSavingCaption(true);
      setError(null);
      const brand = getCurrentBrand();
      const response = await fetch(`${API_ENDPOINTS.IG_IMAGES}/${selectedImage.id}?brand=${brand}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image_url: selectedImage.image_url,
          caption: editedCaption.trim(),
          is_active: selectedImage.is_active,
          category: selectedImage.category || null
        }),
      });

      if (!response.ok) throw new Error('Failed to update caption');
      const updatedImage = await response.json();
      setSelectedImage(updatedImage);
      setEditedCaption(updatedImage.caption || '');
      setImages(prevImages => prevImages.map(img => img.id === updatedImage.id ? updatedImage : img));
    } catch (err) {
      console.error('Error updating Instagram caption:', err);
      setError(err.message || 'Failed to update caption');
    } finally {
      setSavingCaption(false);
    }
  };

  const handleCreateCaption = async () => {
    const caption = newCaption.trim();
    if (!caption) {
      setError('Please enter a caption.');
      return;
    }

    try {
      setSavingNewCaption(true);
      setError(null);
      const response = await fetch(`${API_ENDPOINTS.IG_IMAGES}?brand=${getCurrentBrand()}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image_url: null,
          caption,
          category: newCaptionCategory || null,
          is_active: true
        })
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.message || result.error || 'Failed to save caption');
      }

      setCaptionDialogOpen(false);
      setNewCaption('');
      setNewCaptionCategory('');
      await Promise.all([fetchImages(1), fetchQuota(), fetchCategories()]);
      setCurrentPage(1);
    } catch (err) {
      setError(err.message || 'Failed to save caption');
    } finally {
      setSavingNewCaption(false);
    }
  };

  // Calculate total pages
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const handlePageSizeChange = (e) => {
    setItemsPerPage(e.target.value);
    setCurrentPage(1);
  };

  return (
    <>
      <style>
        {`
          .card-overlay {
            opacity: 0;
            transition: opacity 0.3s ease;
          }
          .MuiCard-root:hover .card-overlay {
            opacity: 1;
          }
          @keyframes loading {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
          @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            25% { transform: translateY(-10px) rotate(1deg); }
            50% { transform: translateY(-20px) rotate(0deg); }
            75% { transform: translateY(-10px) rotate(-1deg); }
          }
          @keyframes shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
          @keyframes shimmer-gradient {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
          .shimmer {
            position: relative;
            overflow: hidden;
          }
          .shimmer::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
            animation: shimmer 2s infinite;
          }
          .pulse-animation {
            animation: pulse 2s ease-in-out infinite;
          }
        `}
      </style>
      <Container
        maxWidth="xl"
        sx={{
          py: { xs: 2, md: 4 },
          px: { xs: 2, md: 3 }
        }}
      >
        {/* Main Content Card Container */}
        <Paper
          elevation={8}
          sx={{
            borderRadius: 4,
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            boxShadow: '0 25px 50px rgba(0,0,0,0.15)',
            border: '1px solid rgba(255,255,255,0.2)'
          }}
        >
          {/* Enhanced Professional Header Section */}
          <Paper
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
              color: 'white',
              borderRadius: 0,
              p: { xs: 3, md: 5 },
              position: 'relative',
              overflow: 'hidden',
              boxShadow: 'none'
            }}
          >
            {/* Animated Background Pattern */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                opacity: 0.08,
                backgroundImage: `
              radial-gradient(circle at 20% 20%, white 1px, transparent 1px),
              radial-gradient(circle at 80% 80%, white 1px, transparent 1px),
              radial-gradient(circle at 40% 40%, white 1px, transparent 1px)
            `,
                backgroundSize: '60px 60px, 80px 80px, 40px 40px',
                animation: 'float 20s ease-in-out infinite'
              }}
            />

            {/* Floating Orbs */}
            <Box
              sx={{
                position: 'absolute',
                top: '10%',
                right: '15%',
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                animation: 'float 15s ease-in-out infinite reverse'
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: '20%',
                left: '10%',
                width: 60,
                height: 60,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.08)',
                backdropFilter: 'blur(8px)',
                animation: 'float 12s ease-in-out infinite'
              }}
            />

            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={{ flexGrow: 1, mb: { xs: 3, md: 0 } }}>
                <Stack direction="row" alignItems="center" spacing={3} sx={{ mb: 2 }}>
                  <Avatar sx={{
                    bgcolor: 'rgba(255,255,255,0.25)',
                    width: { xs: 56, md: 72 },
                    height: { xs: 56, md: 72 },
                    border: '3px solid rgba(255,255,255,0.4)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.05) rotate(5deg)',
                      boxShadow: '0 12px 40px rgba(0,0,0,0.2)'
                    }
                  }}>
                    <ImageIcon sx={{ fontSize: { xs: 28, md: 36 }, color: 'white' }} />
                  </Avatar>
                  <Box>
                    <Typography
                      variant="h2"
                      sx={{
                        fontWeight: 900,
                        mb: 0.5,
                        fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
                        textShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        background: 'linear-gradient(45deg, #ffffff 0%, #f0f8ff 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        letterSpacing: '-0.02em'
                      }}
                    >
                      Instagram Content
                    </Typography>
                    <Typography variant="h5" sx={{
                      opacity: 0.95,
                      fontWeight: 500,
                      fontSize: { xs: '0.9rem', sm: '1rem', md: '1.3rem' },
                      textShadow: '0 2px 10px rgba(0,0,0,0.2)',
                      letterSpacing: '0.01em'
                    }}>
                      Manage reusable captions and optional Instagram media
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ alignItems: 'center' }}>
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={() => setCaptionDialogOpen(true)}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.95)',
                    color: '#2c3e50',
                    px: { xs: 3, md: 4 },
                    py: { xs: 1.5, md: 2.5 },
                    borderRadius: 4,
                    fontWeight: 800,
                    textTransform: 'none',
                    fontSize: { xs: '0.9rem', md: '1.05rem' },
                    minHeight: { xs: 48, md: 64 },
                    '&:hover': { bgcolor: 'white', transform: 'translateY(-3px)' },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Add Caption
                </Button>
                <Button
                  variant="contained"
                  startIcon={<UploadIcon />}
                  onClick={() => setUploadDialogOpen(true)}
                  className="pulse-animation"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.25)',
                    color: 'white',
                    backdropFilter: 'blur(15px)',
                    border: '2px solid rgba(255,255,255,0.4)',
                    px: { xs: 3, md: 5 },
                    py: { xs: 1.5, md: 2.5 },
                    borderRadius: 4,
                    fontWeight: 700,
                    textTransform: 'none',
                    fontSize: { xs: '0.9rem', md: '1.1rem' },
                    minHeight: { xs: 48, md: 64 },
                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.35)',
                      transform: 'translateY(-4px) scale(1.02)',
                      boxShadow: '0 16px 40px rgba(0,0,0,0.25)',
                      borderColor: 'rgba(255,255,255,0.6)',
                      animation: 'none'
                    },
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>{t('igImages.uploadImages')}</Box>
                  <Box sx={{ display: { xs: 'inline', sm: 'none' } }}>{t('common.upload') || 'Upload'}</Box>
                </Button>
              </Stack>
            </Stack>

          </Paper>

          {/* Content Section */}
          <Box sx={{
            p: { xs: 3, md: 4 },
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '1px',
              background: 'linear-gradient(90deg, transparent 0%, rgba(52, 152, 219, 0.3) 50%, transparent 100%)'
            }
          }}>


            {/* Error Alert */}
            {error && (
              <Slide direction="down" in={!!error} mountOnEnter unmountOnExit>
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                    '& .MuiAlert-icon': {
                      fontSize: '1.25rem'
                    }
                  }}
                  action={
                    <IconButton
                      aria-label="close"
                      color="inherit"
                      size="small"
                      onClick={() => setError(null)}
                    >
                      <ErrorIcon fontSize="inherit" />
                    </IconButton>
                  }
                >
                  {error}
                </Alert>
              </Slide>
            )}

            {/* Filter Section - Same Style as Google/Facebook/XHS Pages */}
            <Box sx={{ mb: 4 }}>
              <Paper
                elevation={0}
                sx={{
                  display: 'inline-flex',
                  gap: 0.75,
                  mb: 3,
                  p: 0.75,
                  borderRadius: 3,
                  bgcolor: '#eef2f6',
                  border: '1px solid #dfe6ed'
                }}
              >
                {[
                  { value: 'captions', label: 'Captions', icon: <EditIcon fontSize="small" /> },
                  { value: 'media', label: 'Images & Videos', icon: <ImageIcon fontSize="small" /> }
                ].map((option) => (
                  <Button
                    key={option.value}
                    startIcon={option.icon}
                    onClick={() => {
                      setContentTypeFilter(option.value);
                      setSelectedIds(new Set());
                      setCurrentPage(1);
                    }}
                    sx={{
                      px: { xs: 1.5, sm: 2.5 },
                      py: 1,
                      borderRadius: 2.25,
                      textTransform: 'none',
                      fontWeight: 750,
                      color: contentTypeFilter === option.value ? 'white' : '#52606d',
                      bgcolor: contentTypeFilter === option.value ? '#2c3e50' : 'transparent',
                      boxShadow: contentTypeFilter === option.value ? '0 5px 14px rgba(44,62,80,.2)' : 'none',
                      '&:hover': {
                        bgcolor: contentTypeFilter === option.value ? '#243442' : 'rgba(255,255,255,.8)'
                      }
                    }}
                  >
                    {option.label}
                  </Button>
                ))}
              </Paper>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems="center"
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                <FormControl
                  size="small"
                  sx={{
                    minWidth: { xs: '100%', sm: 140 },
                    width: { xs: '100%', sm: 'auto' },
                    '& .MuiInputLabel-root': {
                      color: '#E4405F',
                      fontWeight: 600,
                      '&.Mui-focused': {
                        color: '#E4405F'
                      }
                    }
                  }}
                >
                  <InputLabel>{t('aiGenerator.contentFilter')}</InputLabel>
                  <Select
                    value={contentFilter}
                    label={t('aiGenerator.contentFilter')}
                    onChange={(e) => {
                      setContentFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        backgroundColor: 'rgba(228, 64, 95, 0.05)',
                        border: '2px solid rgba(228, 64, 95, 0.2)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderColor: 'rgba(228, 64, 95, 0.4)',
                          backgroundColor: 'rgba(228, 64, 95, 0.08)',
                        },
                        '&.Mui-focused': {
                          borderColor: '#E4405F',
                          backgroundColor: 'rgba(228, 64, 95, 0.1)',
                          boxShadow: '0 0 0 3px rgba(228, 64, 95, 0.1)'
                        }
                      },
                      '& .MuiSelect-select': {
                        color: '#2c3e50',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        py: 1.5
                      }
                    }}
                  >
                    <MenuItem
                      value="active"
                      sx={{
                        color: '#27ae60',
                        fontWeight: 600,
                        '&:hover': {
                          backgroundColor: 'rgba(39, 174, 96, 0.08)'
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(39, 174, 96, 0.12)',
                          '&:hover': {
                            backgroundColor: 'rgba(39, 174, 96, 0.16)'
                          }
                        }
                      }}
                    >
                      <CheckCircleIcon sx={{ fontSize: 16, mr: 1, color: '#27ae60' }} />
                      {t('aiGenerator.activeOnly')}
                    </MenuItem>
                    <MenuItem
                      value="inactive"
                      sx={{
                        color: '#e74c3c',
                        fontWeight: 600,
                        '&:hover': {
                          backgroundColor: 'rgba(231, 76, 60, 0.08)'
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(231, 76, 60, 0.12)',
                          '&:hover': {
                            backgroundColor: 'rgba(231, 76, 60, 0.16)'
                          }
                        }
                      }}
                    >
                      <ErrorIcon sx={{ fontSize: 16, mr: 1, color: '#e74c3c' }} />
                      {t('aiGenerator.inactiveOnly')}
                    </MenuItem>
                    <MenuItem
                      value="all"
                      sx={{
                        color: '#E4405F',
                        fontWeight: 600,
                        '&:hover': {
                          backgroundColor: 'rgba(228, 64, 95, 0.08)'
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(228, 64, 95, 0.12)',
                          '&:hover': {
                            backgroundColor: 'rgba(228, 64, 95, 0.16)'
                          }
                        }
                      }}
                    >
                      <AutoAwesomeIcon sx={{ fontSize: 16, mr: 1, color: '#E4405F' }} />
                      {t('aiGenerator.allItems')}
                    </MenuItem>
                  </Select>
                </FormControl>
                <FormControl
                  size="small"
                  sx={{
                    minWidth: { xs: '100%', sm: 160 },
                    width: { xs: '100%', sm: 'auto' },
                    '& .MuiInputLabel-root': {
                      color: '#E4405F',
                      fontWeight: 600,
                      '&.Mui-focused': {
                        color: '#E4405F'
                      }
                    }
                  }}
                >
                  <InputLabel
                    sx={{
                      color: '#833AB4',
                      fontWeight: 600,
                      '&.Mui-focused': {
                        color: '#833AB4'
                      }
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <CategoryIcon sx={{ fontSize: 18 }} />
                      <span>Category Filter</span>
                    </Stack>
                  </InputLabel>
                  <Select
                    value={categoryFilter || 'all'}
                    label="Category Filter"
                    onChange={(e) => {
                      const value = e.target.value === 'all' ? '' : e.target.value;
                      setCategoryFilter(value);
                      setCurrentPage(1);
                    }}
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected || selected === 'all') {
                        return (
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <FilterListIcon sx={{ fontSize: 18, color: '#833AB4' }} />
                            <span style={{ color: '#833AB4', fontWeight: 600 }}>All Categories</span>
                          </Stack>
                        );
                      }
                      return (
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <CategoryIcon sx={{ fontSize: 18, color: '#833AB4' }} />
                          <span style={{ color: '#2c3e50', fontWeight: 600 }}>{selected}</span>
                        </Stack>
                      );
                    }}
                    startAdornment={
                      !categoryFilter || categoryFilter === 'all' ? null : (
                        <CategoryIcon sx={{ fontSize: 18, color: '#833AB4', mr: 1 }} />
                      )
                    }
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        backgroundColor: 'rgba(131, 58, 180, 0.06)',
                        border: '2px solid rgba(131, 58, 180, 0.25)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: '0 2px 8px rgba(131, 58, 180, 0.1)',
                        '&:hover': {
                          borderColor: 'rgba(131, 58, 180, 0.5)',
                          backgroundColor: 'rgba(131, 58, 180, 0.1)',
                          boxShadow: '0 4px 12px rgba(131, 58, 180, 0.15)',
                          transform: 'translateY(-1px)'
                        },
                        '&.Mui-focused': {
                          borderColor: '#833AB4',
                          backgroundColor: 'rgba(131, 58, 180, 0.12)',
                          boxShadow: '0 0 0 4px rgba(131, 58, 180, 0.15), 0 4px 16px rgba(131, 58, 180, 0.2)'
                        }
                      },
                      '& .MuiSelect-select': {
                        color: '#2c3e50',
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        py: 1.5,
                        display: 'flex',
                        alignItems: 'center'
                      },
                      '& .MuiSelect-icon': {
                        color: '#833AB4',
                        fontSize: '1.5rem'
                      }
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          borderRadius: 3,
                          mt: 1,
                          boxShadow: '0 8px 24px rgba(131, 58, 180, 0.2)',
                          border: '1px solid rgba(131, 58, 180, 0.2)',
                          '& .MuiMenuItem-root': {
                            borderRadius: 2,
                            mx: 1,
                            my: 0.5,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: 'rgba(131, 58, 180, 0.1)',
                              transform: 'translateX(4px)'
                            },
                            '&.Mui-selected': {
                              backgroundColor: 'rgba(131, 58, 180, 0.15)',
                              color: '#833AB4',
                              fontWeight: 600,
                              '&:hover': {
                                backgroundColor: 'rgba(131, 58, 180, 0.2)'
                              }
                            }
                          }
                        }
                      }
                    }}
                  >
                    <MenuItem value="all">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <FilterListIcon sx={{ fontSize: 18, color: '#833AB4' }} />
                        <em style={{ fontStyle: 'normal', fontWeight: 600, color: '#833AB4' }}>All Categories</em>
                      </Stack>
                    </MenuItem>
                    {categories.map((cat) => (
                      <MenuItem key={cat} value={cat}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <CategoryIcon sx={{ fontSize: 16, color: '#833AB4', opacity: 0.7 }} />
                          <span>{cat}</span>
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {contentFilter === 'inactive' && images.length > 0 && (
                  <Button
                    variant="contained"
                    startIcon={activatingAll ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                    onClick={handleActivateAllInactive}
                    disabled={activatingAll}
                    sx={{
                      background: 'linear-gradient(135deg, #E4405F 0%, #833AB4 100%)',
                      color: 'white',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #d32f4a 0%, #722a9a 100%)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 20px rgba(228, 64, 95, 0.4)',
                      },
                      '&:disabled': {
                        background: 'linear-gradient(135deg, #f5a5b8 0%, #c19bd9 100%)',
                        color: 'rgba(255, 255, 255, 0.8)',
                      },
                      px: { xs: 2, sm: 3 },
                      py: { xs: 1, sm: 1.25 },
                      borderRadius: { xs: 1.5, sm: 2 },
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: '0.9rem',
                      boxShadow: '0 4px 12px rgba(228, 64, 95, 0.3)',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {activatingAll ? (t('aiGenerator.activatingAll') || 'Activating...') : (t('aiGenerator.activateAllInactive') || 'Activate All')}
                  </Button>
                )}
              </Stack>

              {/* Selection Bar - Appears when items are selected */}
              {selectedIds.size > 0 && (
                <Fade in timeout={300}>
                  <Paper
                    elevation={3}
                    sx={{
                      width: '100%',
                      mt: 2,
                      mb: 4, // Add spacing below
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' }, // Stack on mobile
                      alignItems: { xs: 'stretch', sm: 'center' }, // Stretch on mobile
                      gap: { xs: 2, sm: 2.5 },
                      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                      border: '1px solid #e3f2fd',
                      borderRadius: 3,
                      px: { xs: 2, sm: 3 }, // Reduce padding on mobile
                      py: 2,
                      boxShadow: '0 4px 16px rgba(25, 118, 210, 0.12), 0 2px 4px rgba(0,0,0,0.08)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 3,
                        background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 50%, #1976d2 100%)',
                        backgroundSize: '200% 100%',
                        animation: 'shimmer-gradient 3s ease-in-out infinite'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'space-between', sm: 'flex-start' }, gap: 2, flex: { xs: 'none', sm: 1 }, width: { xs: '100%', sm: 'auto' } }}>
                      <Chip
                        label={`${selectedIds.size} ${selectedIds.size === 1 ? 'item' : 'items'}`}
                        sx={{
                          bgcolor: '#1976d2',
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          height: 28,
                          px: 1,
                          '& .MuiChip-label': {
                            px: 1.5
                          }
                        }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#616161', fontSize: '0.875rem' }}>
                        selected
                      </Typography>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
                        <Tooltip title="Select all (across all pages)" arrow>
                          <IconButton
                            size="small"
                            onClick={selectAllAcrossPages}
                            disabled={images.length === 0 || bulkLoading}
                            sx={{
                              color: '#d32f2f', // Darker red
                              bgcolor: 'rgba(211, 47, 47, 0.1)',
                              border: '1px solid rgba(211, 47, 47, 0.3)',
                              borderRadius: '50%',
                              width: 36,
                              height: 36,
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              '&:hover': {
                                bgcolor: 'rgba(211, 47, 47, 0.2)',
                                borderColor: '#d32f2f',
                                transform: 'scale(1.1)',
                                boxShadow: '0 4px 12px rgba(211, 47, 47, 0.2)'
                              }
                            }}
                          >
                            {bulkLoading ? <CircularProgress size={20} color="inherit" /> : <SelectAllIcon sx={{ fontSize: 20 }} />}
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Select all on current page" arrow>
                          <IconButton
                            size="small"
                            onClick={selectAllOnCurrentPage}
                            disabled={images.length === 0 || bulkLoading}
                            sx={{
                              color: '#1976d2', // Darker blue
                              bgcolor: 'rgba(25, 118, 210, 0.1)',
                              border: '1px solid rgba(25, 118, 210, 0.3)',
                              borderRadius: '50%',
                              width: 36,
                              height: 36,
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              '&:hover': {
                                bgcolor: 'rgba(25, 118, 210, 0.2)',
                                borderColor: '#1976d2',
                                transform: 'scale(1.1)',
                                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)'
                              }
                            }}
                          >
                            <PlaylistAddCheckIcon sx={{ fontSize: 20 }} />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title={t('igImages.clearSelection') || 'Clear selection'} arrow>
                          <IconButton
                            size="small"
                            onClick={clearSelection}
                            sx={{
                              color: '#757575',
                              bgcolor: 'rgba(117, 117, 117, 0.08)',
                              border: '1px solid rgba(117, 117, 117, 0.2)',
                              borderRadius: '50%',
                              width: 36,
                              height: 36,
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              '&:hover': {
                                bgcolor: 'rgba(117, 117, 117, 0.16)',
                                borderColor: '#757575',
                                transform: 'scale(1.1) rotate(-5deg)',
                                boxShadow: '0 4px 12px rgba(117, 117, 117, 0.3)'
                              }
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, flexWrap: 'wrap', justifyContent: { xs: 'stretch', sm: 'flex-end' }, width: { xs: '100%', sm: 'auto' } }}>
                      {(() => {
                        // Check status of selected items
                        const selectedItems = images.filter(item => selectedIds.has(item.id));
                        const hasActiveItems = selectedItems.some(item => item.is_active !== false);
                        const hasInactiveItems = selectedItems.some(item => item.is_active === false);

                        return (
                          <>
                            {hasActiveItems && (
                              <Button
                                size="medium"
                                variant="contained"
                                onClick={handleBulkDeactivate}
                                disabled={bulkLoading}
                                startIcon={bulkLoading ? <CircularProgress size={18} color="inherit" /> : <BlockIcon />}
                                sx={{
                                  flex: { xs: 1, sm: 'none' }, // Expand on mobile
                                  background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                                  color: 'white',
                                  fontWeight: 600,
                                  textTransform: 'none',
                                  px: { xs: 1.5, sm: 2.5 },
                                  py: 1,
                                  borderRadius: 2,
                                  fontSize: '0.875rem',
                                  boxShadow: '0 2px 8px rgba(255, 152, 0, 0.3)',
                                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                  '&:hover': {
                                    background: 'linear-gradient(135deg, #f57c00 0%, #e65100 100%)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 16px rgba(255, 152, 0, 0.4)'
                                  },
                                  '&:disabled': {
                                    background: '#e0e0e0',
                                    color: '#9e9e9e',
                                    boxShadow: 'none'
                                  }
                                }}
                              >
                                {t('igImages.bulkDeactivate') || 'Deactivate Selected'}
                              </Button>
                            )}

                            {hasInactiveItems && (
                              <Button
                                size="medium"
                                variant="contained"
                                onClick={handleBulkActivate}
                                disabled={bulkLoading}
                                startIcon={bulkLoading ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
                                sx={{
                                  flex: { xs: 1, sm: 'none' }, // Expand on mobile
                                  background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                                  color: 'white',
                                  fontWeight: 600,
                                  textTransform: 'none',
                                  px: { xs: 1.5, sm: 2.5 },
                                  py: 1,
                                  borderRadius: 2,
                                  fontSize: '0.875rem',
                                  boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
                                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                  '&:hover': {
                                    background: 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 16px rgba(76, 175, 80, 0.4)'
                                  },
                                  '&:disabled': {
                                    background: '#e0e0e0',
                                    color: '#9e9e9e',
                                    boxShadow: 'none'
                                  }
                                }}
                              >
                                {t('igImages.bulkActivate') || 'Activate Selected'}
                              </Button>
                            )}

                            <Button
                              size="medium"
                              variant="contained"
                              onClick={handleBulkDelete}
                              disabled={bulkLoading}
                              startIcon={bulkLoading ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
                              sx={{
                                flex: { xs: 1, sm: 'none' }, // Expand on mobile
                                background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
                                color: 'white',
                                fontWeight: 600,
                                textTransform: 'none',
                                px: { xs: 1.5, sm: 2.5 },
                                py: 1,
                                borderRadius: 2,
                                fontSize: '0.875rem',
                                boxShadow: '0 2px 8px rgba(244, 67, 54, 0.3)',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                  background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)',
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 4px 16px rgba(244, 67, 54, 0.4)'
                                },
                                '&:disabled': {
                                  background: '#e0e0e0',
                                  color: '#9e9e9e',
                                  boxShadow: 'none'
                                }
                              }}
                            >
                              {t('igImages.bulkDelete') || 'Delete Selected'}
                            </Button>
                          </>
                        );
                      })()}
                    </Box>
                  </Paper>
                </Fade>
              )}
            </Box>

            {/* Enhanced Professional Loading State */}
            {
              loading && (
                <Fade in timeout={300}>
                  <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
                    {[...Array(8)].map((_, index) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4} key={index}>
                        <Card
                          sx={{
                            borderRadius: 4,
                            overflow: 'hidden',
                            transition: 'all 0.3s ease',
                            border: '2px solid transparent',
                            background: 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #2c3e50 0%, #3498db 100%) border-box',
                            animation: 'float 6s ease-in-out infinite',
                            animationDelay: `${index * 0.2}s`,
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: '0 12px 30px rgba(52, 152, 219, 0.15)'
                            }
                          }}
                        >
                          <Box className="shimmer" sx={{ width: '100%', height: { xs: 240, sm: 280, md: 300 }, overflow: 'hidden' }}>
                            <Skeleton
                              variant="rectangular"
                              sx={{
                                width: '100%',
                                height: '100%',
                                background: 'linear-gradient(90deg, #f8f9fa 25%, #e9ecef 50%, #f8f9fa 75%)',
                                backgroundSize: '200% 100%',
                                animation: 'loading 1.8s infinite',
                                borderBottom: '1px solid #e9ecef'
                              }}
                            />
                          </Box>
                          <CardContent sx={{ p: 3 }}>
                            <Stack spacing={2}>
                              <Skeleton variant="text" height={24} width="60%" sx={{ mb: 1 }} />
                              <Skeleton variant="text" height={16} width="80%" sx={{ mb: 2 }} />
                              <Stack direction="row" spacing={1}>
                                <Skeleton variant="rectangular" height={24} width={60} sx={{ borderRadius: 2 }} />
                                <Skeleton variant="rectangular" height={24} width={70} sx={{ borderRadius: 2 }} />
                              </Stack>
                            </Stack>
                          </CardContent>
                          <CardActions sx={{ p: 3, pt: 2, pb: 2, minHeight: 72, justifyContent: 'center' }}>
                            <Skeleton variant="rectangular" height={36} width={120} sx={{ borderRadius: 2 }} />
                          </CardActions>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Fade>
              )
            }

            {/* Enhanced Images Grid */}
            {
              !loading && (
                <Fade in timeout={500}>
                  <Box sx={{
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: -20,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '60%',
                      height: '2px',
                      background: 'linear-gradient(90deg, transparent 0%, rgba(52, 152, 219, 0.2) 50%, transparent 100%)',
                      borderRadius: '2px'
                    }
                  }}>
                    {images.length === 0 ? (
                      <Paper
                        elevation={2}
                        sx={{
                          p: 6,
                          textAlign: 'center',
                          borderRadius: 3,
                          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                          border: '1px solid #dee2e6'
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 80,
                            height: 80,
                            mx: 'auto',
                            mb: 3,
                            bgcolor: 'rgba(52, 152, 219, 0.1)'
                          }}
                        >
                          {contentTypeFilter === 'captions'
                            ? <EditIcon sx={{ fontSize: 40, color: '#d63384' }} />
                            : <ImageIcon sx={{ fontSize: 40, color: '#3498db' }} />}
                        </Avatar>
                        <Typography variant="h5" sx={{ color: '#495057', mb: 2, fontWeight: 600 }}>
                          {contentTypeFilter === 'captions' ? 'No captions found' : 'No images or videos found'}
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#6c757d', mb: 3 }}>
                          {contentTypeFilter === 'captions'
                            ? 'Add a reusable caption for customers to copy and share.'
                            : 'Upload media for brands that use image-based Instagram sharing.'}
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={contentTypeFilter === 'captions' ? <EditIcon /> : <UploadIcon />}
                          onClick={() => contentTypeFilter === 'captions' ? setCaptionDialogOpen(true) : setUploadDialogOpen(true)}
                          sx={{
                            background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #1a252f 0%, #2980b9 100%)'
                            },
                            px: 4,
                            py: 1.5,
                            borderRadius: 2,
                            fontWeight: 600,
                            textTransform: 'none'
                          }}
                        >
                          {contentTypeFilter === 'captions' ? 'Add Caption' : t('igImages.uploadImages')}
                        </Button>
                      </Paper>
                    ) : (
                      <>
                        {contentTypeFilter === 'captions' ? (
                          <Stack spacing={2}>
                            {images.map((image) => (
                              <Paper
                                key={image.id}
                                elevation={0}
                                onClick={() => handleImageClick(image)}
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: { xs: '1fr', md: 'auto minmax(0,1fr) auto' },
                                  alignItems: 'center',
                                  gap: { xs: 2, md: 3 },
                                  p: { xs: 2.25, md: 3 },
                                  border: '1px solid #eadfea',
                                  borderRadius: 3,
                                  bgcolor: '#fff',
                                  cursor: 'pointer',
                                  transition: 'all .2s ease',
                                  '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 10px 25px rgba(89,57,82,.1)', borderColor: '#d8b8d3' }
                                }}
                              >
                                <Box sx={{ alignSelf: { xs: 'start', md: 'center' } }}>
                                  <StatusChip
                                    active={image.is_active !== false}
                                    onToggle={() => handleToggleStatus(image)}
                                    loading={statusUpdatingId === image.id}
                                  />
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                    <Typography variant="overline" sx={{ color: '#d63384', fontWeight: 850, letterSpacing: 1.5 }}>
                                      Instagram caption
                                    </Typography>
                                    {image.category && <Chip label={image.category} size="small" variant="outlined" />}
                                  </Stack>
                                  <Typography
                                    sx={{
                                      color: '#263238',
                                      fontWeight: 600,
                                      lineHeight: 1.65,
                                      whiteSpace: 'pre-line',
                                      display: '-webkit-box',
                                      WebkitLineClamp: 3,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden'
                                    }}
                                  >
                                    {image.caption}
                                  </Typography>
                                </Box>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                  <Button
                                    variant="outlined"
                                    startIcon={<EditIcon />}
                                    onClick={(event) => { event.stopPropagation(); handleImageClick(image); }}
                                    sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}
                                  >
                                    View & Edit
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    color="error"
                                    startIcon={<DeleteIcon />}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleDeleteImage(image.id, true);
                                    }}
                                    sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}
                                  >
                                    Delete
                                  </Button>
                                </Stack>
                              </Paper>
                            ))}
                          </Stack>
                        ) : (
                          <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
                            {images.map((image, index) => (
                              <IgImageCard
                                key={image.id}
                                image={image}
                                index={index}
                                selectedIds={selectedIds}
                                handleImageClick={handleImageClick}
                                handleToggleStatus={handleToggleStatus}
                                statusUpdatingId={statusUpdatingId}
                                t={t}
                                isVideo={isVideo}
                                handleMenuOpen={handleMenuOpen}
                                toggleSelect={toggleSelect}
                              />
                            ))}
                          </Grid>
                        )}

                        {/* Enhanced Pagination */}
                        {totalPages > 1 && (
                          <Paper
                            elevation={2}
                            sx={{
                              p: 3,
                              mt: 4,
                              borderRadius: 3,
                              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                              border: '1px solid #dee2e6'
                            }}
                          >
                            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" spacing={2}>
                              <Typography variant="body2" sx={{ color: '#6c757d', fontWeight: 500 }}>
                                Page {currentPage} of {totalPages} • Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} {contentTypeFilter === 'captions' ? 'captions' : 'media items'}
                              </Typography>
                              <Pagination
                                count={totalPages}
                                page={currentPage}
                                onChange={handlePageChange}
                                color="primary"
                                size="large"
                                sx={{
                                  '& .MuiPaginationItem-root': {
                                    borderRadius: 2,
                                    fontWeight: 600,
                                    '&.Mui-selected': {
                                      background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
                                      color: 'white',
                                      '&:hover': {
                                        background: 'linear-gradient(135deg, #1a252f 0%, #2980b9 100%)'
                                      }
                                    }
                                  }
                                }}
                              />
                              <Box sx={{ minWidth: 120 }}>
                                <FormControl fullWidth size="small">
                                  <InputLabel id="items-per-page-label">Per Page</InputLabel>
                                  <Select
                                    labelId="items-per-page-label"
                                    id="items-per-page"
                                    value={itemsPerPage}
                                    label="Per Page"
                                    onChange={handlePageSizeChange}
                                    sx={{ borderRadius: 2 }}
                                  >
                                    {[10, 20, 30, 40, 50].map((size) => (
                                      <MenuItem key={size} value={size}>
                                        {size}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Box>
                            </Stack>
                          </Paper>
                        )}
                      </>
                    )}
                  </Box>
                </Fade>
              )
            }

            {/* Enhanced Professional Image Detail Dialog */}
            <Dialog
              open={dialogOpen}
              onClose={handleCloseDialog}
              maxWidth="lg"
              fullWidth
              PaperProps={{
                sx: {
                  borderRadius: 4,
                  overflow: 'hidden',
                  boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
                  border: '1px solid rgba(255,255,255,0.1)'
                }
              }}
            >
              {selectedImage && (
                <>
                  <DialogTitle
                    sx={{
                      background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 4,
                      px: 4,
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    {/* Background Pattern */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        opacity: 0.1,
                        backgroundImage: 'radial-gradient(circle at 25% 25%, white 1px, transparent 1px)',
                        backgroundSize: '20px 20px'
                      }}
                    />

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, position: 'relative', zIndex: 1 }}>
                      <Avatar sx={{
                        bgcolor: 'rgba(255,255,255,0.25)',
                        width: 56,
                        height: 56,
                        border: '2px solid rgba(255,255,255,0.3)',
                        boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                      }}>
                        <ImageIcon sx={{ fontSize: 28, color: 'white' }} />
                      </Avatar>
                      <Box>
                        <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5, fontSize: '1.5rem' }}>
                          {selectedImage.image_url ? 'Instagram Image Details' : 'Instagram Caption Details'}
                        </Typography>
                        <Typography variant="body1" sx={{ opacity: 0.95, fontSize: '1rem' }}>
                          {selectedImage.image_url ? 'Image information and metadata' : 'Reusable caption information'}
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton
                      onClick={handleCloseDialog}
                      sx={{
                        color: 'white',
                        bgcolor: 'rgba(255,255,255,0.15)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        '&:hover': {
                          bgcolor: 'rgba(255,255,255,0.25)',
                          transform: 'scale(1.05)'
                        },
                        transition: 'all 0.3s ease',
                        position: 'relative',
                        zIndex: 1
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </DialogTitle>
                  <DialogContent sx={{ p: 0, bgcolor: '#f8f9fa' }}>
                    <Grid container>
                      <Grid item xs={12} md={7}>
                        <Box sx={{
                          p: 4,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minHeight: 500
                        }}>
                          {!selectedImage.image_url ? (
                            <Paper
                              elevation={0}
                              sx={{
                                width: '100%',
                                maxWidth: 620,
                                p: 4,
                                borderRadius: 3,
                                bgcolor: '#fff4fb',
                                border: '1px solid #f2d5e8'
                              }}
                            >
                              <Typography variant="overline" sx={{ color: '#d63384', fontWeight: 800, letterSpacing: 2 }}>
                                Caption-only content
                              </Typography>
                              <Typography sx={{ mt: 2, lineHeight: 1.8, whiteSpace: 'pre-line', fontWeight: 600 }}>
                                {selectedImage.caption}
                              </Typography>
                            </Paper>
                          ) : isVideo(selectedImage.image_url) ? (
                            <Box
                              component="video"
                              src={selectedImage.image_url}
                              controls
                              sx={{
                                maxWidth: '100%',
                                maxHeight: 450,
                                borderRadius: 3,
                                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  transform: 'scale(1.02)',
                                  boxShadow: '0 12px 40px rgba(0,0,0,0.18)'
                                }
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : (
                            <Box
                              component="img"
                              src={selectedImage.image_url}
                              alt={`Instagram Image #${selectedImage.id}`}
                              sx={{
                                maxWidth: '100%',
                                maxHeight: 450,
                                objectFit: 'contain',
                                borderRadius: 3,
                                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  transform: 'scale(1.02)',
                                  boxShadow: '0 12px 40px rgba(0,0,0,0.18)'
                                }
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          )}
                          <Box
                            sx={{
                              width: '100%',
                              height: 400,
                              display: 'none',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                              borderRadius: 3,
                              flexDirection: 'column',
                              border: '2px dashed #dee2e6'
                            }}
                          >
                            <Avatar sx={{
                              bgcolor: 'rgba(52, 152, 219, 0.1)',
                              mb: 3,
                              width: 80,
                              height: 80
                            }}>
                              <ImageIcon sx={{ fontSize: 40, color: '#3498db' }} />
                            </Avatar>
                            <Typography variant="h6" sx={{ color: '#495057', mb: 1, fontWeight: 600 }}>
                              {t('igImages.imageNotAvailable')}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={5}>
                        <Box sx={{
                          p: 4,
                          minHeight: 500,
                          display: 'flex',
                          flexDirection: 'column'
                        }}>
                          <Typography variant="h5" sx={{
                            mb: 3,
                            color: '#2c3e50',
                            fontWeight: 700,
                            borderBottom: '2px solid #3498db',
                            pb: 1
                          }}>
                            {selectedImage.image_url ? 'Instagram Image Details' : 'Instagram Caption Details'}
                          </Typography>

                          <Stack spacing={3}>
                            {/* Caption records are managed independently from image records. */}
                            {!selectedImage.image_url && <Box>
                              <Typography variant="subtitle2" sx={{ color: '#6c757d', mb: 1, fontWeight: 600 }}>
                                Instagram caption
                              </Typography>
                              <TextField
                                value={editedCaption}
                                onChange={(event) => setEditedCaption(event.target.value.slice(0, 2200))}
                                multiline
                                minRows={5}
                                fullWidth
                                placeholder="Write the reusable caption users can copy"
                                helperText={`${editedCaption.length} / 2200 characters`}
                                disabled={savingCaption}
                                sx={{
                                  '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: 'white' }
                                }}
                              />
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={savingCaption ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                                onClick={handleSaveCaption}
                                disabled={savingCaption}
                                sx={{ mt: 1.5, borderRadius: 2, textTransform: 'none' }}
                              >
                                {savingCaption ? 'Saving caption...' : 'Save caption'}
                              </Button>
                            </Box>}

                            {/* Category */}
                            <Box>
                              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ color: '#6c757d', fontWeight: 600 }}>
                                  Category
                                </Typography>
                                {!isEditingCategory && (
                                  <IconButton
                                    size="small"
                                    onClick={handleStartEditCategory}
                                    sx={{
                                      color: '#3498db',
                                      '&:hover': {
                                        bgcolor: 'rgba(52, 152, 219, 0.1)',
                                        transform: 'scale(1.1)'
                                      },
                                      transition: 'all 0.2s ease'
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </Stack>
                              {isEditingCategory ? (
                                <Stack spacing={2}>
                                  <Autocomplete
                                    freeSolo
                                    options={categories}
                                    value={editedCategory || null}
                                    onChange={(event, newValue) => {
                                      setEditedCategory(newValue || '');
                                    }}
                                    onInputChange={(event, newInputValue) => {
                                      setEditedCategory(newInputValue);
                                    }}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        variant="outlined"
                                        placeholder="Enter or select category"
                                        sx={{
                                          '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            backgroundColor: 'white',
                                            '&:hover': {
                                              '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#3498db'
                                              }
                                            },
                                            '&.Mui-focused': {
                                              '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#3498db',
                                                borderWidth: '2px'
                                              }
                                            }
                                          }
                                        }}
                                      />
                                    )}
                                  />
                                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      startIcon={<CancelIcon />}
                                      onClick={handleCancelEditCategory}
                                      disabled={savingCategory}
                                      sx={{
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        borderColor: '#6c757d',
                                        color: '#6c757d',
                                        '&:hover': {
                                          borderColor: '#495057',
                                          bgcolor: 'rgba(108, 117, 125, 0.1)'
                                        }
                                      }}
                                    >
                                      Cancel
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      startIcon={savingCategory ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                                      onClick={handleSaveCategory}
                                      disabled={savingCategory}
                                      sx={{
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
                                        boxShadow: '0 4px 12px rgba(52, 152, 219, 0.3)',
                                        '&:hover': {
                                          background: 'linear-gradient(135deg, #2980b9 0%, #1e6fa8 100%)',
                                          boxShadow: '0 6px 16px rgba(52, 152, 219, 0.4)',
                                          transform: 'translateY(-1px)'
                                        },
                                        transition: 'all 0.2s ease'
                                      }}
                                    >
                                      {savingCategory ? 'Saving...' : 'Save'}
                                    </Button>
                                  </Stack>
                                </Stack>
                              ) : (
                                <Box>
                                  {selectedImage.category ? (
                                    <Chip
                                      label={selectedImage.category}
                                      icon={<CategoryIcon sx={{ fontSize: 16 }} />}
                                      sx={{
                                        fontWeight: 600,
                                        fontSize: '0.9rem',
                                        height: 32,
                                        background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
                                        color: 'white',
                                        boxShadow: '0 4px 12px rgba(52, 152, 219, 0.3)',
                                        '& .MuiChip-icon': {
                                          color: 'white'
                                        }
                                      }}
                                    />
                                  ) : (
                                    <Typography variant="body2" sx={{ color: '#6c757d', fontStyle: 'italic' }}>
                                      No category assigned
                                    </Typography>
                                  )}
                                </Box>
                              )}
                            </Box>

                            {/* Created Date */}
                            <Box>
                              <Typography variant="subtitle2" sx={{ color: '#6c757d', mb: 1, fontWeight: 600 }}>
                                {t('igImages.createdDate')}
                              </Typography>
                              <Typography variant="body1" sx={{ color: '#2c3e50', fontWeight: 600, mb: 0.5 }}>
                                {new Date(selectedImage.created_at).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#6c757d' }}>
                                {new Date(selectedImage.created_at).toLocaleTimeString('en-US', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </Typography>
                            </Box>

                            {selectedImage.updated_at !== selectedImage.created_at && (
                              <Box>
                                <Typography variant="subtitle2" sx={{ color: '#6c757d', mb: 1, fontWeight: 600 }}>
                                  {t('igImages.lastUpdated')}
                                </Typography>
                                <Typography variant="body1" sx={{ color: '#2c3e50', fontWeight: 600, mb: 0.5 }}>
                                  {new Date(selectedImage.updated_at).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                  })}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#6c757d' }}>
                                  {new Date(selectedImage.updated_at).toLocaleTimeString('en-US', {
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </Typography>
                              </Box>
                            )}

                            {/* Image URL is only relevant to media records. */}
                            {selectedImage.image_url && <Box>
                              <Typography variant="subtitle2" sx={{ color: '#6c757d', mb: 1, fontWeight: 600 }}>
                                {t('igImages.imageUrl')}
                              </Typography>
                              <Box
                                sx={{
                                  p: 2,
                                  bgcolor: '#f8f9fa',
                                  borderRadius: 2,
                                  border: '1px solid #dee2e6',
                                  wordBreak: 'break-all',
                                  fontFamily: 'monospace',
                                  fontSize: '0.85rem',
                                  color: '#495057',
                                  lineHeight: 1.4
                                }}
                              >
                                {selectedImage.image_url}
                              </Box>
                            </Box>}
                          </Stack>
                        </Box>
                      </Grid>
                    </Grid>
                  </DialogContent>
                </>
              )}
            </Dialog>

            {/* Context Menu */}
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem
                onClick={() => {
                  if (selectedImage) {
                    handleDeleteImage(selectedImage.id);
                  }
                  handleMenuClose();
                }}
                sx={{ color: 'error.main' }}
              >
                <DeleteIcon sx={{ mr: 1 }} />
                {t('igImages.deleteImage')}
              </MenuItem>
            </Menu>

            <Dialog
              open={captionDialogOpen}
              onClose={() => !savingNewCaption && setCaptionDialogOpen(false)}
              maxWidth="sm"
              fullWidth
              PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}
            >
              <DialogTitle sx={{ bgcolor: '#2c3e50', color: 'white', fontWeight: 800 }}>
                Add Instagram Caption
              </DialogTitle>
              <DialogContent sx={{ pt: '28px !important' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                  Save a ready-to-use caption. BYD customers can copy and share it without selecting a photo.
                </Typography>
                <TextField
                  autoFocus
                  fullWidth
                  multiline
                  minRows={7}
                  label="Caption"
                  value={newCaption}
                  onChange={(event) => setNewCaption(event.target.value.slice(0, 2200))}
                  helperText={`${newCaption.length} / 2200`}
                />
                <Autocomplete
                  freeSolo
                  options={categories}
                  value={newCaptionCategory}
                  onInputChange={(_, value) => setNewCaptionCategory(value)}
                  renderInput={(params) => (
                    <TextField {...params} label="Category (optional)" sx={{ mt: 3 }} />
                  )}
                />
              </DialogContent>
              <DialogActions sx={{ p: 3 }}>
                <Button onClick={() => setCaptionDialogOpen(false)} disabled={savingNewCaption}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleCreateCaption}
                  disabled={savingNewCaption || !newCaption.trim()}
                  startIcon={savingNewCaption ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                >
                  {savingNewCaption ? 'Saving…' : 'Save Caption'}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Enhanced Professional Upload Dialog */}
            <Dialog
              open={uploadDialogOpen}
              onClose={handleCloseUploadDialog}
              maxWidth="md"
              fullWidth
              PaperProps={{
                sx: {
                  borderRadius: 4,
                  overflow: 'hidden',
                  boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
                  border: '1px solid rgba(255,255,255,0.1)'
                }
              }}
            >
              <DialogTitle
                sx={{
                  background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 4,
                  px: 4,
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {/* Background Pattern */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    opacity: 0.1,
                    backgroundImage: 'radial-gradient(circle at 25% 25%, white 1px, transparent 1px)',
                    backgroundSize: '20px 20px'
                  }}
                />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, position: 'relative', zIndex: 1 }}>
                  <Avatar sx={{
                    bgcolor: 'rgba(255,255,255,0.25)',
                    width: 56,
                    height: 56,
                    border: '2px solid rgba(255,255,255,0.3)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                  }}>
                    <UploadIcon sx={{ fontSize: 28, color: 'white' }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5, fontSize: '1.5rem' }}>
                      {t('igImages.uploadDialogTitle')}
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.95, fontSize: '1rem' }}>
                      {t('igImages.uploadDialogSubtitle')}
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  onClick={handleCloseUploadDialog}
                  sx={{
                    color: 'white',
                    bgcolor: 'rgba(255,255,255,0.15)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.25)',
                      transform: 'scale(1.05)'
                    },
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    zIndex: 1
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent sx={{ p: 4 }}>
                <Stack spacing={3}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                      border: '1px solid #dee2e6'
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 2, color: '#2c3e50', fontWeight: 600 }}>
                      {t('igImages.uploadRequirements')}
                    </Typography>
                    <Stack spacing={1}>
                      <Typography variant="body2" sx={{ color: '#6c757d', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon sx={{ fontSize: 16, color: '#3498db' }} />
                        {t('igImages.supportedFormats')} (Images: JPG, PNG, GIF, WebP | Videos: MP4, MOV, AVI, WebM)
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#6c757d', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon sx={{ fontSize: 16, color: '#3498db' }} />
                        {t('igImages.maxFileSize')}
                      </Typography>
                    </Stack>
                  </Paper>

                  {/* Quota Display */}
                  <Paper
                    elevation={1}
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      background: quota.used_quota >= quota.quota_limit
                        ? 'linear-gradient(135deg, rgba(244, 67, 54, 0.1) 0%, rgba(220, 53, 69, 0.1) 100%)'
                        : quota.used_quota >= quota.quota_limit * 0.8
                          ? 'linear-gradient(135deg, rgba(255, 193, 7, 0.1) 0%, rgba(255, 152, 0, 0.1) 100%)'
                          : 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(52, 152, 219, 0.1) 100%)',
                      border: `2px solid ${quota.used_quota >= quota.quota_limit
                        ? '#f44336'
                        : quota.used_quota >= quota.quota_limit * 0.8
                          ? '#ffc107'
                          : '#4caf50'}`
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ color: '#6c757d', mb: 0.5, fontWeight: 600 }}>
                          Upload Quota
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            color: quota.used_quota >= quota.quota_limit ? '#f44336' : '#2c3e50',
                            fontWeight: 700
                          }}
                        >
                          {quota.used_quota.toLocaleString()} / {quota.quota_limit.toLocaleString()}
                        </Typography>
                      </Box>
                      {quota.used_quota >= quota.quota_limit * 0.8 && (
                        <Chip
                          label={quota.used_quota >= quota.quota_limit ? 'Full' : 'Low'}
                          size="medium"
                          sx={{
                            backgroundColor: quota.used_quota >= quota.quota_limit
                              ? '#f44336'
                              : '#ffc107',
                            color: 'white',
                            fontWeight: 700,
                            fontSize: '0.85rem',
                            height: 32
                          }}
                        />
                      )}
                    </Stack>
                    <Box sx={{ mt: 2 }}>
                      <LinearProgress
                        variant="determinate"
                        value={(quota.used_quota / quota.quota_limit) * 100}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: 'rgba(0, 0, 0, 0.1)',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: quota.used_quota >= quota.quota_limit
                              ? '#f44336'
                              : quota.used_quota >= quota.quota_limit * 0.8
                                ? '#ffc107'
                                : '#4caf50',
                            borderRadius: 4
                          }
                        }}
                      />
                    </Box>
                    {quota.used_quota >= quota.quota_limit && (
                      <Typography variant="caption" sx={{ color: '#f44336', mt: 1, display: 'block', fontWeight: 600 }}>
                        Quota limit reached. Please contact administrator to increase your quota.
                      </Typography>
                    )}
                  </Paper>

                  {/* Category Selection */}
                  <Autocomplete
                    freeSolo
                    options={categories}
                    value={selectedCategory || null}
                    onChange={(event, newValue) => {
                      setSelectedCategory(newValue || '');
                    }}
                    onInputChange={(event, newInputValue) => {
                      setSelectedCategory(newInputValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Category (Optional)"
                        variant="outlined"
                        helperText="Select an existing category or type a new one"
                        placeholder="e.g., Food, Service, Product"
                      />
                    )}
                  />

                  <input
                    accept="image/*,video/*"
                    style={{ display: 'none' }}
                    id="image-upload"
                    type="file"
                    multiple
                    onChange={handleFileSelect}
                  />
                  <Button
                    component="label"
                    htmlFor="image-upload"
                    variant="outlined"
                    fullWidth
                    sx={{
                      p: 4,
                      border: '2px dashed #3498db',
                      borderRadius: 3,
                      background: selectedFiles.length > 0
                        ? 'linear-gradient(135deg, rgba(52, 152, 219, 0.05) 0%, rgba(41, 128, 185, 0.05) 100%)'
                        : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        borderColor: '#2980b9',
                        backgroundColor: selectedFiles.length > 0
                          ? 'linear-gradient(135deg, rgba(52, 152, 219, 0.1) 0%, rgba(41, 128, 185, 0.1) 100%)'
                          : 'linear-gradient(135deg, rgba(52, 152, 219, 0.05) 0%, rgba(41, 128, 185, 0.05) 100%)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 25px rgba(52, 152, 219, 0.15)'
                      }
                    }}
                  >
                    <Stack alignItems="center" spacing={2}>
                      <Avatar
                        sx={{
                          width: 64,
                          height: 64,
                          bgcolor: selectedFiles.length > 0
                            ? 'rgba(52, 152, 219, 0.1)'
                            : 'rgba(52, 152, 219, 0.05)'
                        }}
                      >
                        <UploadIcon
                          sx={{
                            fontSize: 32,
                            color: selectedFiles.length > 0 ? '#3498db' : '#6c757d'
                          }}
                        />
                      </Avatar>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography
                          variant="h6"
                          sx={{
                            color: selectedFiles.length > 0 ? '#3498db' : '#495057',
                            fontWeight: 600,
                            mb: 1
                          }}
                        >
                          {selectedFiles.length > 0
                            ? `${selectedFiles.length} ${selectedFiles.length > 1 ? t('igImages.filesSelectedPlural') : t('igImages.filesSelected')}`
                            : t('igImages.clickToSelect')
                          }
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#6c757d' }}>
                          {selectedFiles.length > 0
                            ? t('igImages.readyToUpload')
                            : t('igImages.dragDropHint')
                          }
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#adb5bd', mt: 1, display: 'block' }}>
                          {t('igImages.maxFileSizeHint')}
                        </Typography>
                      </Box>
                    </Stack>
                  </Button>

                  {selectedFiles.length > 0 && (
                    <Paper
                      elevation={1}
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        border: '1px solid #dee2e6'
                      }}
                    >
                      <Typography variant="h6" sx={{ mb: 2, color: '#2c3e50', fontWeight: 600 }}>
                        {t('igImages.selectedFiles')} ({selectedFiles.length})
                      </Typography>
                      <Box sx={{ maxHeight: 250, overflowY: 'auto' }}>
                        <Stack spacing={2}>
                          {selectedFiles.map((file, index) => (
                            <Paper
                              key={index}
                              elevation={0}
                              sx={{
                                p: 2,
                                bgcolor: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                                borderRadius: 2,
                                border: '1px solid #dee2e6'
                              }}
                            >
                              <Stack direction="row" alignItems="center" spacing={2}>
                                <Avatar sx={{ bgcolor: 'rgba(52, 152, 219, 0.1)', width: 40, height: 40 }}>
                                  <ImageIcon sx={{ color: '#3498db', fontSize: 20 }} />
                                </Avatar>
                                <Box sx={{ flexGrow: 1 }}>
                                  <Typography variant="body1" sx={{ color: '#2c3e50', fontWeight: 600, mb: 0.5 }}>
                                    {file.name}
                                  </Typography>
                                  <Stack direction="row" spacing={2} alignItems="center">
                                    <Chip
                                      label={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
                                      size="small"
                                      variant="outlined"
                                      sx={{ fontSize: '0.75rem' }}
                                    />
                                    <Chip
                                      label={file.type.split('/')[1]?.toUpperCase() || 'IMAGE'}
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                      sx={{ fontSize: '0.75rem' }}
                                    />
                                  </Stack>
                                </Box>
                                <CheckCircleIcon sx={{ color: '#3498db', fontSize: 24 }} />
                              </Stack>
                            </Paper>
                          ))}
                        </Stack>
                      </Box>
                    </Paper>
                  )}

                  {uploadingFiles.length > 0 && (
                    <Paper
                      elevation={1}
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        border: '1px solid #dee2e6'
                      }}
                    >
                      <Typography variant="h6" sx={{ mb: 2, color: '#2c3e50', fontWeight: 600 }}>
                        {t('igImages.uploadProgress')}
                      </Typography>
                      <Stack spacing={2}>
                        {uploadingFiles.map((file, index) => (
                          <Paper
                            key={index}
                            elevation={0}
                            sx={{
                              p: 2,
                              bgcolor: file.status === 'completed'
                                ? 'linear-gradient(135deg, rgba(40, 167, 69, 0.05) 0%, rgba(32, 201, 151, 0.05) 100%)'
                                : file.status === 'error'
                                  ? 'linear-gradient(135deg, rgba(220, 53, 69, 0.05) 0%, rgba(255, 107, 107, 0.05) 100%)'
                                  : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                              borderRadius: 2,
                              border: `1px solid ${file.status === 'completed' ? '#28a745' : file.status === 'error' ? '#dc3545' : '#dee2e6'}`
                            }}
                          >
                            <Stack direction="row" alignItems="center" spacing={2}>
                              <Avatar sx={{
                                bgcolor: file.status === 'completed'
                                  ? 'rgba(52, 152, 219, 0.1)'
                                  : file.status === 'error'
                                    ? 'rgba(220, 53, 69, 0.1)'
                                    : 'rgba(52, 152, 219, 0.1)',
                                width: 40,
                                height: 40
                              }}>
                                {file.status === 'completed' ? (
                                  <CheckCircleIcon sx={{ color: '#3498db', fontSize: 20 }} />
                                ) : file.status === 'error' ? (
                                  <ErrorIcon sx={{ color: '#dc3545', fontSize: 20 }} />
                                ) : (
                                  <CircularProgress size={20} sx={{ color: '#3498db' }} />
                                )}
                              </Avatar>
                              <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="body1" sx={{ color: '#2c3e50', fontWeight: 600, mb: 0.5 }}>
                                  {file.name}
                                </Typography>
                                {file.status === 'uploading' && (
                                  <LinearProgress
                                    sx={{
                                      height: 6,
                                      borderRadius: 3,
                                      bgcolor: 'rgba(102, 126, 234, 0.1)',
                                      '& .MuiLinearProgress-bar': {
                                        bgcolor: '#3498db'
                                      }
                                    }}
                                  />
                                )}
                                {file.error && (
                                  <Typography variant="caption" sx={{ color: '#dc3545', mt: 1, display: 'block' }}>
                                    {file.error}
                                  </Typography>
                                )}
                              </Box>
                              <Typography variant="caption" sx={{
                                color: file.status === 'completed' ? '#3498db' :
                                  file.status === 'error' ? '#dc3545' : '#6c757d',
                                fontWeight: 600
                              }}>
                                {file.status === 'completed' ? t('igImages.completed') :
                                  file.status === 'error' ? t('igImages.failed') : t('igImages.uploadingProgress')}
                              </Typography>
                            </Stack>
                          </Paper>
                        ))}
                      </Stack>
                    </Paper>
                  )}

                  {uploading && (
                    <Paper
                      elevation={1}
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        border: '1px solid #3498db',
                        bgcolor: 'linear-gradient(135deg, rgba(52, 152, 219, 0.05) 0%, rgba(41, 128, 185, 0.05) 100%)'
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                        <CircularProgress size={24} sx={{ color: '#3498db' }} />
                        <Typography variant="h6" sx={{ color: '#2c3e50', fontWeight: 600 }}>
                          {t('igImages.uploadingToR2')}
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={uploadProgress}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: 'rgba(52, 152, 219, 0.1)',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: '#3498db'
                          }
                        }}
                      />
                      <Typography variant="body2" sx={{ color: '#6c757d', mt: 1, textAlign: 'center' }}>
                        {uploadProgress.toFixed(0)}{t('igImages.percentComplete')}
                      </Typography>
                    </Paper>
                  )}
                </Stack>
              </DialogContent>
              <DialogActions sx={{ p: 4, bgcolor: '#f8f9fa' }}>
                <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                  <Box sx={{ flexGrow: 1 }} />
                  <Button
                    onClick={handleUpload}
                    variant="contained"
                    disabled={selectedFiles.length === 0 || uploading}
                    startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
                    sx={{
                      background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #1a252f 0%, #2980b9 100%)',
                        transform: 'translateY(-1px)'
                      },
                      borderRadius: 2,
                      px: 4,
                      py: 1.5,
                      fontWeight: 600,
                      color: 'white !important',
                      textTransform: 'none',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {uploading
                      ? `${t('igImages.uploadingProgress')} (${uploadProgress.toFixed(0)}%)`
                      : `${t('igImages.uploadAndAdd')} ${selectedFiles.length} ${selectedFiles.length > 1 ? t('igImages.uploadAndAddFilesPlural') : t('igImages.uploadAndAddFiles')}`
                    }
                  </Button>
                </Stack>
              </DialogActions>
            </Dialog>

            {/* Duplicate Image Confirmation Dialog */}
            <Dialog
              open={duplicateDialogOpen}
              onClose={() => setDuplicateDialogOpen(false)}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle sx={{
                bgcolor: '#fff3cd',
                color: '#856404',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <ErrorIcon color="inherit" />
                {t('storeImages.duplicateImageDetected')}
              </DialogTitle>
              <DialogContent sx={{ mt: 2 }}>
                <Typography gutterBottom>
                  {t('storeImages.duplicateImageMessage')}
                </Typography>
                <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto', mt: 1, bgcolor: '#f8f9fa' }}>
                  <ul style={{ margin: 0, padding: '8px 8px 8px 24px' }}>
                    {duplicateFiles.map((fileName, index) => (
                      <li key={index}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {fileName}
                        </Typography>
                      </li>
                    ))}
                  </ul>
                </Paper>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDuplicateDialogOpen(false)} color="inherit">
                  {t('common.cancel') || 'Cancel'}
                </Button>
                <Button
                  onClick={handleSkipDuplicates}
                  variant="contained"
                  color="info"
                  sx={{
                    background: 'linear-gradient(135deg, #17a2b8 0%, #138496 100%)',
                    color: 'white'
                  }}
                >
                  {t('storeImages.skipDuplicates')}
                </Button>
                <Button
                  onClick={handleConfirmUpload}
                  variant="contained"
                  color="primary"
                  sx={{
                    background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)'
                  }}
                >
                  {t('storeImages.uploadAnyway')}
                </Button>
              </DialogActions>
            </Dialog>
          </Box >
        </Paper >
      </Container >
    </>
  );
};

export default IgImages;
